import React from 'react';
import {useIntl} from 'react-intl';
import {StageBandBar} from '../../opportunities/StageBandBar';
import {
	deriveOpportunityStage,
	OPPORTUNITY_STAGE_MESSAGE_IDS,
	winProbabilityBand,
	type OpportunityStageKey,
} from '../../../../opportunities-tab/opportunityStage';

// The five selectable stage bands. Each maps to a canonical win chance (a representative value inside its
// range); 0% (Lost) and 100% (Won) are set through the row's Set-0% / Set-100% quick actions, not this
// control. Each band's displayed % range is taken from winProbabilityBand(value), so the picker always
// matches the list's Win Probability column and the deriveOpportunityStage boundaries.
const BANDS: {stage: OpportunityStageKey; value: number}[] = [
	{stage: 'NEW_LEAD', value: 0.1},
	{stage: 'DISCOVERY', value: 0.2},
	{stage: 'QUALIFIED_OPPORTUNITY', value: 0.4},
	{stage: 'PROPOSAL_SENT', value: 0.6},
	{stage: 'CONTRACT_SENT', value: 0.9},
];

type Props = {value: number; onChange: (value: number) => void};

// Renders the win-probability picker as stage bands. The band highlighted is the one whose stage the
// current win chance falls in (so a mid-band value like 65% still lights the "Proposal" band), and
// picking a band emits that band's canonical value.
export const WinProbabilityStageBands = ({value, onChange}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const selected = BANDS.find(b => b.stage === deriveOpportunityStage(value));

	return (
		<StageBandBar
			value={selected ? String(selected.value) : ''}
			onChange={next => onChange(Number(next))}
			dataCy="win-probability"
			items={BANDS.map(band => ({
				value: String(band.value),
				topText: winProbabilityBand(band.value).label,
				label: formatMessage({id: OPPORTUNITY_STAGE_MESSAGE_IDS[band.stage]}),
				dataCy: `win-band-${band.stage}`,
			}))}
		/>
	);
};

export default WinProbabilityStageBands;
